import React, { useState, useEffect } from 'react';
import {
    styled,
    Box,
    CardActions,
    FormControlLabel,
    Radio,
    RadioGroup,
    Switch,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { ActionButton } from 'components/ui/buttons';
import { useTextInput } from 'hooks/forms';
import { validateEmail } from 'utility/validation';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SendIcon from '@mui/icons-material/Send';
import { TextArea, TextField } from 'components/ui/inputs';

export default function TicketEmailer(props) {
    const { tranId, eventId, email, multiTicket, onSend } = props;

    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [defaultHostNote, setDefaultHostNote] = useState(null);

    const { notify } = useNotification();

    useEffect(() => {
        fetchExpresso(`/apiv2/events/${eventId}/host_note`)
            .then((res) => {
                if (res.status === 200) {
                    return res.json();
                } else {
                    throw new Error();
                }
            })
            .then((host) => {
                setDefaultHostNote(host.hostNote);
                setReady(true);
            })
            .catch((e) => {
                setReady(true);
            });
    }, [eventId]);

    const handleSend = ({ email, overrideHostNote, hostNote, delivery, locale }) => {
        setLoading(true);

        fetchExpresso(`/apiv2/events/${eventId}/orders/${tranId}?delivery=${delivery}`, {
            method: 'POST',
            body: {
                tranId: tranId,
                email: email,
                overrideHostNote: overrideHostNote,
                hostNote: hostNote,
                locale: locale,
            },
        })
            .then((res) => {
                if (res.status === 200) {
                    notify.success(`Sent tickets to: ${email}`);

                    // Create a log on successful send
                    fetchExpresso(`/apiv2/ticket_activity/`, {
                        method: 'POST',
                        body: { tranId: tranId, typeId: 100, email: email },
                    })
                        .then(() => {
                            if (onSend) {
                                onSend();
                            }
                        })
                        .catch(() => null);
                } else {
                    throw new Error();
                }
            })
            .catch(() => notify.error('There was a problem sending tickets'))
            .finally(() => setLoading(false));
    };

    if (ready) {
        return (
            <TicketEmailerForm
                tranId={tranId}
                loading={loading}
                email={email}
                multiTicket={multiTicket}
                defaultHostNote={defaultHostNote}
                onSend={handleSend}
            />
        );
    } else {
        return (
            <Card>
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center">
                            <AlternateEmailIcon />
                            <Title>Send Tickets by Email</Title>
                        </Box>
                    }
                    subheader="Send these tickets to a specific email address"
                />
                <CardContent>
                    <Box
                        minHeight={multiTicket ? '160px' : '60px'}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }
}

function TicketEmailerForm(props) {
    const { loading, multiTicket, defaultHostNote, onSend } = props;

    const [delivery, setDelivery] = useState('pdf'); // pdf, zip
    const [locale, setLocale] = useState('en');
    const [overrideHostNote, setOverrideHostNote] = useState(false);

    const email = useTextInput({ defaultValue: props.email });
    const hostNote = useTextInput({ defaultValue: defaultHostNote, maxLength: 2000 });

    const handleSubmit = () => {
        let formError = false;

        if (validateEmail(email.value) === false) {
            email.setError('Please enter a valid email address');
            formError = true;
        }

        if (formError) return;

        onSend({
            email: email.value.trim(),
            overrideHostNote: overrideHostNote,
            hostNote: overrideHostNote ? hostNote.value.trim() : null,
            delivery: delivery,
            locale: locale,
        });
    };

    return (
        <Card>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center">
                        <AlternateEmailIcon />
                        <Title>Send Tickets by Email</Title>
                    </Box>
                }
                subheader="Send these tickets to a specific email address"
            />
            <CardContent>
                <Box minHeight={multiTicket ? '160px' : '60px'}>
                    <Box mb={2}>
                        <TextField type="email" label="Email Address" {...email.formProps} />
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={overrideHostNote}
                                onChange={(e) => setOverrideHostNote(e.target.checked)}
                            />
                        }
                        label="Include a Personal Message"
                    />

                    {overrideHostNote === true && (
                        <Box mt={2}>
                            <TextArea
                                {...hostNote.formProps}
                                label="Personalized Message"
                            />
                        </Box>
                    )}

                    {multiTicket && (
                        <Box mt={3}>
                            <Typography style={{ fontWeight: 'bold' }}>
                                How should the tickets be attached?
                            </Typography>
                            <RadioGroup
                                aria-label="bundle"
                                value={delivery}
                                onChange={(e) => setDelivery(e.target.value)}
                            >
                                <FormControlLabel
                                    value="pdf"
                                    control={<Radio />}
                                    label={'Combine all tickets into one PDF'}
                                />
                                <Box mt={1} />
                                <FormControlLabel
                                    value="zip"
                                    control={<Radio />}
                                    label={
                                        <>
                                            <Typography>
                                                Send each ticket as a separate PDF
                                            </Typography>
                                            <Typography variant="caption">
                                                Best when the customer is sharing tickets with other
                                                people.
                                            </Typography>
                                        </>
                                    }
                                />
                            </RadioGroup>
                        </Box>
                    )}

                    <Box mt={3}>
                        <Typography style={{ fontWeight: 'bold' }}>
                            Email Language
                        </Typography>
                        <RadioGroup
                            row
                            aria-label="locale"
                            value={locale}
                            sx={{ marginRight: 'auto' }}
                            onChange={(e) => setLocale(e.target.value)}
                        >
                            <FormControlLabel
                                value="en"
                                control={<Radio />}
                                label="English"
                            />
                            <FormControlLabel
                                value="fr"
                                control={<Radio />}
                                label="French"
                            />
                        </RadioGroup>
                    </Box>
                </Box>
            </CardContent>
            <CardActions>
                <ActionButton disabled={loading} onClick={handleSubmit} startIcon={<SendIcon />}>
                    {loading ? 'Sending...' : 'Send Tickets'}
                </ActionButton>
            </CardActions>
        </Card>
    );
}

const Title = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
    display: 'inline',
}));
Title.defaultProps = { variant: 'h6' };
